import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  isError,
  listSources,
  presignUpload,
  readExtracted,
  type ExtractedContent,
  type Source,
} from './api';

/**
 * Adding material, and seeing what was read out of it.
 *
 * The second half is the point. A photograph of a page is only worth uploading if
 * you can tell whether it was read correctly — an extraction nobody checks is a
 * confidently wrong curriculum waiting to happen.
 */

/**
 * What the file picker will offer, matching what the API will sign.
 *
 * **HEIC is deliberately absent, and its absence is the feature.** Textract
 * refuses HEIC outright — measured, on a real one — and an iPhone writes HEIC by
 * default. When the picker asks only for JPEG, iOS converts on the way out, so
 * leaving HEIC off this list is what makes photographing a page from an iPhone
 * work at all. Listing it made the upload succeed and the reading fail.
 */
const ACCEPT = 'image/jpeg,image/png,image/tiff,application/pdf';

/**
 * What the browser says a file is, when it says anything.
 *
 * Some phone browsers hand over an empty `type` for a file picked out of the
 * photo library, and an empty content type is refused by the presign as
 * unsupported — so a perfectly good photograph is rejected for a reason that has
 * nothing to do with it. The extension is the fallback, and it is the same thing
 * the server would key off anyway.
 */
const BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  pdf: 'application/pdf',
};

function typeOf(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[extension] ?? '';
}

/** Megabytes, for a sentence rather than for arithmetic. */
function megabytes(bytes: number): string {
  return String(Math.floor(bytes / 1024 / 1024));
}

/** Polling stops here. Extraction is seconds for an image, longer for a PDF. */
const POLL_ATTEMPTS = 40;
const POLL_INTERVAL_MS = 3000;

/**
 * The id the gap notice sends the learner to.
 *
 * Exported rather than spelled out in both files. The notice on the objective
 * screen finds this panel by id and scrolls to it, and a literal written twice is
 * one rename away from a button that silently does nothing — which is the worst
 * shape of failure for a control whose entire job is to lead somewhere.
 */
export const MATERIAL_PANEL_ID = 'your-material';

export default function Sources({
  objectiveId,
  /** True when the objective has gaps, which changes what this panel is for. */
  wanted = false,
}: {
  objectiveId: string;
  wanted?: boolean;
}) {
  const { t } = useI18n();
  const [sources, setSources] = useState<Source[] | undefined>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [open, setOpen] = useState<Record<string, ExtractedContent | 'loading' | undefined>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const result = await listSources(objectiveId);
    if (!isError(result)) setSources(result.sources);
    else setSources([]);
    return isError(result) ? [] : result.sources;
  }, [objectiveId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Waits for extraction to finish, then stops.
   *
   * Polling rather than a push, because the alternative is a websocket for one
   * event a learner is already watching for. It gives up rather than spinning
   * forever: a source stuck at `pending` past the timeout is a real failure, and
   * saying so beats a spinner that never resolves.
   */
  async function waitForExtraction(sourceId: string) {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      const current = await load();
      const source = current.find((s) => s.sourceId === sourceId);
      if (source && source.status !== 'pending') return;
    }
  }

  async function upload(file: File) {
    setBusy(true);
    setProblem(undefined);

    const signed = await presignUpload(objectiveId, typeOf(file), file.name, file.size);
    if (isError(signed)) {
      setBusy(false);
      /*
       * Each refusal gets its own sentence, because each has its own remedy.
       * "Something went wrong with that file" over a HEIC photograph sends a
       * learner to look for a bug that is not there — the fix is a camera
       * setting, and it takes one line to say so.
       */
      setProblem(
        signed.error === 'camera_format'
          ? t('src.error.camera')
          : signed.error === 'too_large'
            ? `${t('src.error.tooLarge')} ${megabytes(signed.maxBytes ?? 0)} MB.`
            : signed.error === 'unsupported_type'
              ? t('src.error.type')
              : t('src.error.generic'),
      );
      return;
    }

    try {
      /*
       * Straight to S3, with the same content type that was signed.
       *
       * `file.type` rather than `typeOf(file)` stored `binary/octet-stream` on
       * every upload from a browser that reports no type — the object is still
       * extractable, because routing is by extension, but F-46 reads this field
       * to send the crop back to a phone and a wrong one there is a broken image.
       */
      const response = await fetch(signed.url, {
        method: 'PUT',
        headers: { 'content-type': typeOf(file) },
        body: file,
      });
      if (!response.ok) throw new Error(`S3 returned ${response.status}`);
    } catch {
      setBusy(false);
      setProblem(t('src.error.upload'));
      return;
    }

    await load();
    await waitForExtraction(signed.sourceId);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function toggle(sourceId: string) {
    if (open[sourceId]) {
      setOpen((prior) => ({ ...prior, [sourceId]: undefined }));
      return;
    }
    setOpen((prior) => ({ ...prior, [sourceId]: 'loading' }));
    const result = await readExtracted(objectiveId, sourceId);
    setOpen((prior) => ({
      ...prior,
      [sourceId]: isError(result) ? undefined : result.content,
    }));
  }

  return (
    /*
     * `tabIndex` so the gap notice can move focus here and not only the viewport.
     * A button that scrolls the page and leaves focus behind strands a keyboard or
     * screen-reader user on the notice they just acted on, with the control they
     * were sent to nowhere near them.
     */
    <section className="panel" id={MATERIAL_PANEL_ID} tabIndex={-1}>
      <h2>{t('src.title')}</h2>
      {/* "Optional" is the wrong first word for somebody who arrived from the gap
          notice: it invites them to reconsider the one thing on this screen that
          only they can do. */}
      <p className="muted">{wanted ? t('src.hint.wanted') : t('src.hint')}</p>

      <div className="actions">
        <label className="button primary file-button">
          {busy ? t('src.working') : t('src.choose')}
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
        </label>
      </div>

      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}

      {sources?.length === 0 && <p className="muted">{t('src.empty')}</p>}

      <ul className="sources">
        {sources?.map((source) => (
          <li key={source.sourceId}>
            <p className="meta">
              <span className={`pill pill-${source.status}`}>
                {t(`src.status.${source.status}` as CopyKey)}
              </span>{' '}
              {source.title ?? source.sourceId}
            </p>

            {source.status === 'failed' && (
              /*
               * Named, not generalised: somebody who photographed eight pages
               * needs to know which one was unreadable, and why, in their own
               * language. The reason is a code with a sentence written for it;
               * `error` is English and quotes AWS, so it is the last resort
               * rather than the first — it once showed a learner an IAM policy
               * denial with a role ARN in it.
               */
              <p className="muted">
                {source.reason
                  ? t(`src.failed.${source.reason}` as CopyKey)
                  : (source.error ?? t('src.error.generic'))}
              </p>
            )}

            {source.status === 'extracted' && (
              <>
                <button type="button" className="linky" onClick={() => void toggle(source.sourceId)}>
                  {open[source.sourceId] ? t('src.hide') : t('src.show')}
                </button>
                {open[source.sourceId] === 'loading' && <p className="muted">{t('src.loading')}</p>}
                {open[source.sourceId] && open[source.sourceId] !== 'loading' && (
                  <>
                    <p className="muted small">
                      {(open[source.sourceId] as ExtractedContent).method} ·{' '}
                      {(open[source.sourceId] as ExtractedContent).body.length}{' '}
                      {t('src.characters')}
                    </p>
                    <pre className="extracted">
                      {(open[source.sourceId] as ExtractedContent).body}
                      {(open[source.sourceId] as ExtractedContent).truncated
                        ? `\n\n… ${t('src.truncated')}`
                        : ''}
                    </pre>
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
